<template>
  <b-modal
      id="alerts-navarea-modal"
      hide-header-close
      hide-footer
      modal-class="alerts-navarea-modal"
      dialog-class="alerts-navarea-modal__dialog"
      content-class="alerts-navarea-modal__content"
      header-class="alerts-navarea-modal__header"
      body-class="alerts-navarea-modal__body"
  >
    <template #modal-title>
      <div class="navarea-title">
        <span class="title-label">{{ headerLabel }}</span>
        <span class="title-meta">{{ headerMeta }}</span>
      </div>
    </template>

    <div class="modal-body-content">
      <div class="message-block">
        <p v-for="(line, index) in bodyLines" :key="`body-${index}`" class="message-line">
          {{ line }}
        </p>
      </div>

      <div v-if="hasAlerts" class="alerts-block">
        <div
            v-for="alert in normalizedAlerts"
            :key="alert.id"
            :class="['navarea-card', alert.severityClass]"
        >
          <div class="navarea-card__header">
            <span class="navarea-card__label">{{ alert.label }}</span>
            <span class="navarea-card__meta">{{ alert.timestamp }}</span>
          </div>
          <div class="navarea-card__message">
            <p v-for="(line, idx) in alert.messageLines" :key="`msg-${alert.id}-${idx}`">
              {{ line }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-actions">
      <b-button
          variant="outline-secondary"
          size="sm"
          class="action-button"
          @click="handleClose"
      >
        Close
      </b-button>
    </div>
  </b-modal>
</template>

<script>
export default {
  props: {
    payload: {
      type: Object,
      default: null,
    },
  },
  computed: {
    hasAlerts() {
      return Array.isArray(this.payload?.alerts) && this.payload.alerts.length > 0
    },
    dominantSeverity() {
      if (!this.hasAlerts) return 'info'
      const colors = this.payload.alerts.map(alert => this.normalizeColor(alert.color))
      if (colors.includes('red')) return 'critical'
      if (colors.includes('yellow')) return 'warning'
      if (colors.includes('green')) return 'info'
      return 'info'
    },
    headerLabel() {
      switch (this.dominantSeverity) {
        case 'critical':
          return 'Navarea danger detected'
        case 'warning':
          return 'Navarea warnings nearby'
        default:
          return 'Navarea information'
      }
    },
    headerMeta() {
      const shipLabel = this.payload?.shipName || 'Fleet overview'
      const updatedAt = this.payload?.updatedAt
      if (updatedAt) {
        const date = new Date(updatedAt)
        if (!Number.isNaN(date.getTime())) {
          return `${shipLabel} · Updated ${date.toLocaleString()}`
        }
      }
      return shipLabel
    },
    bodyLines() {
      if (!this.hasAlerts) {
        return [
          'No detected Navarea warnings near the vessel.',
          'Monitoring continues automatically and new warnings will be shown here.',
        ]
      }

      const total = this.payload.alerts.length
      const severeCount = this.payload.alerts.filter(alert => this.normalizeColor(alert.color) === 'red').length
      const warningCount = this.payload.alerts.filter(alert => this.normalizeColor(alert.color) === 'yellow').length

      const lines = []
      lines.push(`Detected ${total} Navarea warning${total === 1 ? '' : 's'} near the vessel.`)

      if (severeCount > 0) {
        lines.push(`${severeCount} warning${severeCount === 1 ? '' : 's'} indicate immediate navigational danger.`)
      }

      if (warningCount > 0) {
        lines.push(`${warningCount} warning${warningCount === 1 ? '' : 's'} require attention during route planning.`)
      }

      lines.push('Review the warnings below and adjust the route or safety procedures if needed.')
      return lines
    },
    normalizedAlerts() {
      if (!this.hasAlerts) return []

      return this.payload.alerts.map((alert, index) => {
        const message = alert.message || ''
        const messageLines = message
          .split(/\r?\n/)
          .map(line => line.trim())
          .filter(Boolean)

        const timestamp = this.formatTimestamp(alert.timestamp)
        const severityClass = this.mapSeverityClass(this.normalizeColor(alert.color))

        const fallbackLabel = this.payload.alerts.length === 1 ? 'Navarea warning' : `Navarea warning ${index + 1}`
        let label = alert.title ? alert.title.trim() : fallbackLabel

        if (/^navarea warning/i.test(label)) {
          const suffix = label.replace(/^navarea warning/i, '').trim()
          label = suffix ? `Navarea warning ${suffix}` : 'Navarea warning'
        }

        if (!label) {
          label = fallbackLabel
        }

        return {
          id: alert.id || `navarea-${index}`,
          label,
          messageLines: messageLines.length > 0 ? messageLines : ['No additional details provided.'],
          timestamp,
          severityClass,
        }
      })
    },
  },
  watch: {
    payload: {
      handler(newVal) {
        if (newVal) {
          this.$nextTick(() => {
            this.$bvModal.show('alerts-navarea-modal')
          })
        } else {
          this.$bvModal.hide('alerts-navarea-modal')
        }
      },
      immediate: true,
    },
  },
  methods: {
    handleClose() {
      this.$bvModal.hide('alerts-navarea-modal')
      this.$emit('close')
    },
    normalizeColor(color) {
      if (!color) return 'nocl'
      const normalized = color.toString().toLowerCase()
      if (normalized.includes('red')) return 'red'
      if (normalized.includes('yellow')) return 'yellow'
      if (normalized.includes('green')) return 'green'
      return 'nocl'
    },
    mapSeverityClass(color) {
      switch (color) {
        case 'red':
          return 'navarea-card--danger'
        case 'yellow':
          return 'navarea-card--warning'
        case 'green':
          return 'navarea-card--info'
        default:
          return 'navarea-card--neutral'
      }
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleString()
    },
  },
}
</script>

<style scoped>
.navarea-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
}

.title-label {
  color: #1f2940;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.title-meta {
  color: #5b6480;
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.modal-body-content {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 4px 2px 8px;
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
  color: #1f2940;
}

.message-block {
  background: linear-gradient(135deg, rgba(250, 251, 255, 0.95), rgba(234, 237, 255, 0.9));
  border: 1px solid rgba(209, 214, 233, 0.7);
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 14px 28px rgba(24, 32, 60, 0.12);
}

.message-line {
  margin: 0 0 10px;
  font-size: 0.87rem;
  font-weight: 500;
  line-height: 1.4;
  color: #1f2940;
}

.message-line:last-child {
  margin-bottom: 0;
}

.alerts-block {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.navarea-card {
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid rgba(218, 222, 240, 0.6);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 12px 24px rgba(24, 32, 60, 0.12);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.navarea-card__header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
}

.navarea-card__label {
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #394063;
}

.navarea-card__meta {
  font-size: 0.72rem;
  color: #6b7290;
}

.navarea-card__message {
  font-size: 0.86rem;
  line-height: 1.35;
  color: #1f2940;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.navarea-card__message p {
  margin: 0;
}

.navarea-card--danger {
  border-color: rgba(234, 69, 86, 0.35);
  background: rgba(255, 240, 243, 0.92);
  box-shadow: 0 18px 32px rgba(220, 70, 94, 0.15);
}

.navarea-card--warning {
  border-color: rgba(255, 193, 58, 0.35);
  background: rgba(255, 248, 229, 0.92);
  box-shadow: 0 18px 32px rgba(221, 167, 66, 0.15);
}

.navarea-card--info {
  border-color: rgba(64, 181, 142, 0.28);
  background: rgba(240, 252, 247, 0.94);
  box-shadow: 0 18px 32px rgba(64, 181, 142, 0.12);
}

.navarea-card--neutral {
  border-color: rgba(198, 205, 230, 0.38);
  background: rgba(247, 249, 255, 0.94);
  box-shadow: 0 18px 32px rgba(117, 126, 160, 0.12);
}

.modal-actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.action-button {
  min-width: 120px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 600;
}

.action-button.primary {
  background: linear-gradient(135deg, #4b5fdd, #5669f5);
  border-color: rgba(75, 95, 221, 0.2);
}

::v-deep .alerts-navarea-modal__dialog {
  max-width: 640px;
}

::v-deep .alerts-navarea-modal__content {
  border-radius: 18px;
  border: none;
  box-shadow: 0 24px 40px rgba(20, 30, 55, 0.18);
}

::v-deep .alerts-navarea-modal__header {
  background: linear-gradient(135deg, #f4f6ff 0%, #e6e9ff 100%);
  border-bottom: none;
  padding: 18px 22px;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  box-shadow: inset 0 -1px 0 rgba(98, 111, 149, 0.12);
}

::v-deep .alerts-navarea-modal__body {
  padding: 20px 24px 24px;
}
</style>
